import {
	ClientPortfolioDomainEnum,
	type ClientPropertyItemResponseDTO,
	ClientPropertyTypeEnum,
	ClientPropertyVehicleTypeEnum,
	type PortfolioListItemResponseDTO,
	type PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getTitle } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { propertyCarTitle } from "web_myplann/Portfolio/Helpers/propertyCarTitle";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { formatNumber } from "web_myplann/Shared/Utils/numberFormatters";
import { normalizeChange } from "web_myplann/Shared/Utils/percentageFormatters";

type TranslateFunction = ReturnType<typeof useResource>["t"];

export type DashboardDetailRowData = {
	id: number;
	logo?: string | null;
	domain?: ClientPortfolioDomainEnum | null;
	propertyType?: ClientPropertyTypeEnum | null;
	name: string;
	subtitle?: string;
	extra?: string;
	valueText?: string;
	currencySign?: string;
	change?: number | null;
	navigateTo: string;
};

const INSURANCE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.LifeInsurance,
	ClientPortfolioDomainEnum.PropertyInsurance,
]);
const INVESTMENT_DOMAINS = new Set([
	ClientPortfolioDomainEnum.PensionProducts,
	ClientPortfolioDomainEnum.BuildingSavings,
	ClientPortfolioDomainEnum.Investments,
]);
const LOAN_DOMAINS = new Set([
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.Leasing,
]);
const ASSET_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Commodities,
	ClientPortfolioDomainEnum.BankingProducts,
	ClientPortfolioDomainEnum.Cryptocurrencies,
]);
const SERVICE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Services,
	ClientPortfolioDomainEnum.PaidAdvisory,
]);
const REAL_ESTATE_TYPES = new Set([
	ClientPropertyTypeEnum.Flat,
	ClientPropertyTypeEnum.House,
	ClientPropertyTypeEnum.Cottage,
	ClientPropertyTypeEnum.Land,
]);

const mapPortfolioRow = (
	data: PortfolioListItemResponseDTO,
	reportingData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
): DashboardDetailRowData => {
	const domain = data.domain ?? null;
	const base: DashboardDetailRowData = {
		id: data.id,
		logo: data.icon,
		domain,
		name: getTitle(data, t),
		subtitle: data.producer ?? undefined,
		navigateTo: Routing.Portfolio.PortfolioDetail.path(data.id.toString()),
	};

	if (domain === null) return base;

	if (INSURANCE_DOMAINS.has(domain)) {
		if (data.startAt) {
			const endDate = data.endAt
				? dateFormatter(data.endAt)
				: t(Resources.Common.NotSpecified);
			base.extra = `${dateFormatter(data.startAt)} - ${endDate}`;
		}
		return base;
	}

	if (SERVICE_DOMAINS.has(domain)) {
		base.extra = data.contractNumber ?? undefined;
		return base;
	}

	const summary = reportingData?.find((item) => item.id === data.id)?.summary;
	const hasValue =
		INVESTMENT_DOMAINS.has(domain) ||
		ASSET_DOMAINS.has(domain) ||
		LOAN_DOMAINS.has(domain);
	if (!summary || !hasValue) return base;

	base.valueText = formatNumber(summary.totalValue ?? 0);
	base.currencySign = getPortfolioCurrencySign(t, summary.currency);
	if (!LOAN_DOMAINS.has(domain)) {
		base.change = normalizeChange(summary.totalEvaluationPercent);
	}

	return base;
};

const getPropertyName = (
	data: ClientPropertyItemResponseDTO,
	t: TranslateFunction,
) => {
	const property = data.property ?? null;

	return (
		data.name ??
		(property !== null
			? t(Resources.PortfolioDetail.Property.PropertyType[property])
			: "")
	);
};

const mapPropertyRow = (
	data: ClientPropertyItemResponseDTO,
	t: TranslateFunction,
): DashboardDetailRowData => {
	const currencySign = t(Resources.Common.CurrencySign);
	const property = data.property ?? null;
	const base: DashboardDetailRowData = {
		id: data.id,
		propertyType: property,
		name: getPropertyName(data, t),
		navigateTo: Routing.Portfolio.PropertyDetail.path(data.id.toString()),
	};

	if (property === ClientPropertyTypeEnum.AutoMoto) {
		base.name =
			data.propertyType &&
			data.propertyType !== ClientPropertyVehicleTypeEnum.None
				? t(
						Resources.PortfolioDetail.Property.CarPropertyTypeValue[
							data.propertyType as ClientPropertyVehicleTypeEnum
						],
					)
				: t(Resources.PortfolioDetail.Property.PropertyType.AutoMoto);
		base.subtitle = propertyCarTitle(
			data.carBrand,
			data.carModel,
			data.carLicensePlate,
		);
		if (data.value) {
			base.valueText = formatNumber(Number(data.value));
			base.currencySign = currencySign;
		}
		return base;
	}

	if (property !== null && REAL_ESTATE_TYPES.has(property)) {
		base.subtitle =
			[data.location, data.locationMunicipality, data.locationZipCode]
				.filter(Boolean)
				.join(", ") || undefined;
		if (data.marketValue) {
			base.valueText = formatNumber(Number(data.marketValue));
			base.currencySign = currencySign;
		}
		return base;
	}

	base.subtitle = data.propertyType ?? undefined;
	if (data.value) {
		base.valueText = formatNumber(Number(data.value));
		base.currencySign = currencySign;
	}
	return base;
};

export const mapDashboardDetailRows = (
	portfolios: PortfolioListItemResponseDTO[] | undefined,
	properties: ClientPropertyItemResponseDTO[] | undefined,
	reportingData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
): DashboardDetailRowData[] => [
	...(portfolios ?? []).map((data) => mapPortfolioRow(data, reportingData, t)),
	...(properties ?? []).map((data) => mapPropertyRow(data, t)),
];
